/**
 * SplashScreen — v3.0 (Bold Authority)
 *
 * Design: Option E — A's commanding 800-weight DM Sans meets D's uppercase restraint.
 * Uppercase "TRUEPRICE" on cream, grounded by a teal underline that draws in.
 *
 * Industry benchmarks (Apple HIG, Material Design 3, Mercury/Stripe, Copilot):
 *  - ≤800ms animation before signaling ready
 *  - Solid background matching app theme (cream #FAF8F3)
 *  - Typography-forward, no logo mark
 *  - Fade-up entrance + draw-line accent
 *  - Respect prefers-reduced-motion
 *
 * Duration: 800ms before onReady callback
 * Exit: Parent wraps in opacity transition; this component doesn't self-dismiss
 */

import { useEffect, useRef } from 'react';
import { T } from '@/styles/tokens';

interface SplashScreenProps {
  onReady?: () => void;
}

// ── Inline keyframes (injected once) ────────────────────────────

const STYLE_ID = 'tp-splash-keyframes';

function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes tpSplashFadeUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes tpSplashDrawLine {
      from { width: 0; }
      to   { width: 48px; }
    }
    @media (prefers-reduced-motion: reduce) {
      @keyframes tpSplashFadeUp   { from, to { opacity: 1; transform: none; } }
      @keyframes tpSplashDrawLine { from, to { width: 48px; } }
    }
  `;
  document.head.appendChild(style);
}

// ── Component ───────────────────────────────────────────────────

export function SplashScreen({ onReady }: SplashScreenProps) {
  const calledRef = useRef(false);

  useEffect(() => { ensureKeyframes(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!calledRef.current) {
        calledRef.current = true;
        onReady?.();
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [onReady]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: T.cream,
        fontFamily: T.fontFamily,
      }}
    >
      {/* Wordmark */}
      <div
        style={{
          fontSize: '34px',
          fontWeight: 800,
          letterSpacing: '0.03em',
          color: T.text,
          lineHeight: 1,
          textTransform: 'uppercase' as const,
          animation: 'tpSplashFadeUp 500ms cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        TRUEPRICE
      </div>

      {/* Teal underline */}
      <div
        style={{
          marginTop: '14px',
          height: '2.5px',
          borderRadius: '2px',
          backgroundColor: T.primary,
          animation: 'tpSplashDrawLine 400ms cubic-bezier(0.32, 0.72, 0, 1) 200ms both',
        }}
      />
    </div>
  );
}

export default SplashScreen;
